import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useApi } from '../../hooks/useApi';
import AccountProfileEditor from './AccountProfileEditor';

// Reusable right-side audit-finding slide-out ("blade"). Self-contained:
// give it an auditId and it fetches the full cma.audit doc and renders the
// same sections the Audit Center findings page shows — question header,
// answer status, reviewer/auditor perspective, auditor override, detailed
// findings, remediation, metadata, raw JSON fallback, and the Account
// Profile editor.
//
// Props:
//   open       : boolean — slide in/out
//   auditId    : string  — cma.audit doc id to load
//   onClose    : () => void
//   onPrev     : optional () => void  (renders the < nav button)
//   onNext     : optional () => void
//   indexLabel : optional string, e.g. "3/8" shown between the nav arrows
//   onChanged  : optional () => void  — called after a re-queue / override save
function AuditFindingBlade({ open, auditId, onClose, onPrev, onNext, indexLabel, onChanged }) {
  const theme = useTheme();
  const jApi = useApi();

  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [override, setOverride] = useState('');
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !auditId) { setDetails(null); return; }
      try {
        setIsLoading(true);
        setOverride('');
        const resp = await jApi.request('GET', `cma/audit?id=${encodeURIComponent(auditId)}`);
        const data = Array.isArray(resp.data) && resp.data.length > 0 ? resp.data[0] : resp.data;
        if (!cancelled) setDetails(data || null);
      } catch (ex) {
        if (!cancelled) {
          setDetails(null);
          setToast(ex?.toString?.() || 'Failed to load audit details');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, auditId]);

  const reload = async () => {
    if (!auditId) return;
    try {
      const resp = await jApi.request('GET', `cma/audit?id=${encodeURIComponent(auditId)}`);
      const data = Array.isArray(resp.data) && resp.data.length > 0 ? resp.data[0] : resp.data;
      setDetails(data || null);
    } catch { /* leave prior details */ }
  };

  const runAudit = async () => {
    if (!details?.functionPath) return;
    try {
      setIsRunning(true);
      // Splat keys match exactly what cma/qbr/Start-QbrCycleAudits.ps1
      // queues so a re-run from the blade is identical to the original:
      //   @{ account=…; auditRunId=…; auditDate=…; auditId=… }
      const resp = await jApi.request('POST', 'ves/job', {
        functionpath: details.functionPath,
        splat: {
          account:    details.account,
          auditRunId: details.auditRunId,
          auditDate:  details.auditDate,
          auditId:    details.id,
        },
      });
      const jobId = resp?.data?.id;
      if (jobId) {
        await jApi.request('PUT', 'cma/audit', { id: details.id, jobId });
        setToast(`Audit re-queued (job ${jobId})`);
        reload();
        onChanged && onChanged();
      } else {
        setToast('No job id returned from VES');
      }
    } catch (ex) {
      setToast(ex?.toString?.() || 'Failed to re-queue audit');
    } finally {
      setIsRunning(false);
    }
  };

  const saveOverride = async () => {
    if (!details || !override.trim()) return;
    try {
      setIsSavingOverride(true);
      await jApi.request('PUT', 'cma/audit', { id: details.id, userPrompt: override });
      setToast('Auditor override saved');
      setOverride('');
      reload();
      onChanged && onChanged();
    } catch (ex) {
      setToast(ex?.toString?.() || 'Failed to save override');
    } finally {
      setIsSavingOverride(false);
    }
  };

  const answer = (details?.answer || '').toLowerCase();
  const statusIcon = answer === 'pass'
    ? <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
    : answer === 'fail' || answer === 'error'
    ? <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
    : <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />;
  const answerChipColor = answer === 'pass' ? 'success'
    : answer === 'fail' || answer === 'error' ? 'error'
    : answer ? 'warning' : 'default';

  const hasStructured = details && (details.question || details.title || details.answer);

  return (
    <>
      {/* Scrim */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed', inset: 0,
          bgcolor: 'rgba(0,0,0,0.4)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.3s',
          zIndex: 1299,
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: open ? 0 : '-820px',
          width: { xs: '95vw', md: '820px' },
          height: '100%',
          bgcolor: 'background.paper',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.3)',
          transition: 'right 0.35s ease-in-out',
          zIndex: 1300,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {open && (
          <Box sx={{ p: 4 }}>
            {/* Header: nav + close */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              {(onPrev || onNext) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, userSelect: 'none' }}>
                  <IconButton size="small" onClick={onPrev} disabled={!onPrev}
                    sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 2, width: 36, height: 36 }}>
                    <ChevronLeftIcon />
                  </IconButton>
                  {indexLabel && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 70, textAlign: 'center' }}>
                      {indexLabel}
                    </Typography>
                  )}
                  <IconButton size="small" onClick={onNext} disabled={!onNext}
                    sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 2, width: 36, height: 36 }}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              ) : <Box />}
              <IconButton onClick={onClose} size="large" aria-label="close" sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </Box>

            {isLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading audit details…
                </Typography>
              </Box>
            )}

            {!isLoading && hasStructured && (
              <>
                {/* Question header */}
                <Paper elevation={3} sx={{
                  p: 3, mb: 3, borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                    {details.section && <Chip label={details.section} size="small" color="primary" sx={{ fontWeight: 600 }} />}
                    <Chip label={details.answer || 'PENDING'} size="small" color={answerChipColor} />
                    <Box sx={{ flex: 1 }} />
                    {details.functionPath && (
                      <Button variant="contained" size="small" onClick={runAudit}
                        disabled={isRunning || isLoading} sx={{ minWidth: 120 }}>
                        {isRunning ? (<><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Re-running…</>) : 'Re-run'}
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: details.question ? 2 : 0 }}>
                    {details.title || 'Audit Result'}
                  </Typography>
                  {details.question && (
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{details.question}"
                    </Typography>
                  )}
                  {details.jobId && (
                    <Chip label={`Job ID: ${details.jobId}`} size="small" variant="outlined" color="info" sx={{ mt: 2 }} />
                  )}
                </Paper>

                {/* Answer status */}
                {details.answer && (
                  <Paper elevation={2} sx={{
                    p: 3, mb: 3, borderRadius: 2,
                    bgcolor: answer === 'pass'
                      ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                      : answer === 'fail' || answer === 'error'
                      ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                      : alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                    border: `2px solid ${answer === 'pass' ? theme.palette.success.main
                      : answer === 'fail' || answer === 'error' ? theme.palette.error.main
                      : theme.palette.warning.main}`,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {statusIcon}
                      <Box>
                        <Typography variant="h6" fontWeight="bold">Status: {details.answer}</Typography>
                        {details.ai?.confidence !== undefined && (
                          <Typography variant="body2" color="text.secondary">
                            Confidence: {details.ai.confidence}%
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {/* Findings: comments + perspectives + override + account profile */}
                {(details.ai?.reviewerPerspective || details.ai?.auditorPerspective || details.comments
                  || (details.account && details.functionPath)) && (
                  <Paper elevation={2} sx={{
                    p: 3, mb: 3, borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Findings</Typography>
                    <Box sx={{ mt: 2 }}>
                      {details.comments && (
                        <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                          {details.comments}
                        </Typography>
                      )}
                      {details.ai?.reviewerPerspective && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.info.main, 0.08), borderLeft: `4px solid ${theme.palette.info.main}` }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>👤 Reviewer Perspective</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{details.ai.reviewerPerspective}</Typography>
                        </Paper>
                      )}
                      {details.ai?.auditorPerspective && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), borderLeft: `4px solid ${theme.palette.warning.main}` }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>🔍 Auditor Perspective</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{details.ai.auditorPerspective}</Typography>
                        </Paper>
                      )}

                      {/* Auditor override (this run only) */}
                      <Accordion sx={{ mb: 2, bgcolor: alpha(theme.palette.info.main, 0.05), '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: alpha(theme.palette.info.main, 0.08) }}>
                          <Typography variant="subtitle2" fontWeight="bold">✏️ Auditor Override</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Typography variant="caption" color="text.secondary">
                              Custom instructions appended to the AI prompt when this audit is processed.
                            </Typography>
                            {details.ai?.userPrompt && (
                              <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                                <Typography variant="caption" fontWeight="bold" color="success.main">Current Override:</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{details.ai.userPrompt}</Typography>
                              </Paper>
                            )}
                            <TextField multiline rows={4} fullWidth variant="outlined"
                              placeholder="Enter custom instructions for the AI prompt…"
                              value={override} onChange={(e) => setOverride(e.target.value)} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button variant="contained" size="small" onClick={saveOverride}
                                disabled={isSavingOverride || !override.trim()}
                                startIcon={isSavingOverride ? <CircularProgress size={16} /> : <SaveIcon />}>
                                {isSavingOverride ? 'Saving…' : 'Save Override'}
                              </Button>
                            </Box>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>

                      {/* Persistent customer context injected into matching audits */}
                      {details.account && details.functionPath && (
                        <AccountProfileEditor
                          account={details.account}
                          customer={details.account_name || details.customer}
                          functionPath={details.functionPath}
                        />
                      )}
                    </Box>
                  </Paper>
                )}

                {/* Detailed findings */}
                {details.ai?.detailedFindings && (
                  <Accordion defaultExpanded={false} sx={{ mb: 3, borderRadius: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight="bold">📋 Detailed Findings</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3, bgcolor: alpha(theme.palette.background.paper, 0.8) }}>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                        {details.ai.detailedFindings.split('\n').map((line, idx) => (
                          <Box key={idx} sx={{ display: 'block', mb: 0.5 }}>{line || ' '}</Box>
                        ))}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Remediation */}
                {(details.ai?.remediationType || details.ai?.remediationDetails) && (
                  <Paper elevation={2} sx={{
                    p: 3, mb: 3, borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                    border: `2px solid ${theme.palette.warning.main}`,
                  }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.main">🔧 Remediation Required</Typography>
                    {details.ai?.remediationType && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Type:</Typography>
                        <Chip label={details.ai.remediationType} size="small" color="warning" sx={{ mt: 0.5 }} />
                      </Box>
                    )}
                    {details.ai?.remediationDetails && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Details:</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{details.ai.remediationDetails}</Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Metadata */}
                <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                  <Stack spacing={1}>
                    {details.auditRunId && (
                      <Box><Typography variant="caption" color="text.secondary" fontWeight="bold">Audit Run ID:</Typography>
                        <Typography variant="body2">{details.auditRunId}</Typography></Box>
                    )}
                    {details.auditType && (
                      <Box><Typography variant="caption" color="text.secondary" fontWeight="bold">Audit Type:</Typography>
                        <Typography variant="body2">{details.auditType}</Typography></Box>
                    )}
                    {details.account && (
                      <Box><Typography variant="caption" color="text.secondary" fontWeight="bold">Account:</Typography>
                        <Typography variant="body2">{details.account}</Typography></Box>
                    )}
                  </Stack>
                  {details.ai?.reasoningTrace && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Reasoning Trace:</Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {details.ai.reasoningTrace}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            )}

            {/* Raw fallback */}
            {!isLoading && details && !hasStructured && (
              <Paper elevation={3} sx={{
                p: 3, mb: 4, borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                border: `2px solid ${theme.palette.warning.main}`,
              }}>
                <Typography variant="h6" color="warning.main" gutterBottom>Raw Data</Typography>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2, maxHeight: 600, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(details, null, 2)}
                  </pre>
                </Paper>
              </Paper>
            )}

            {!isLoading && !details && (
              <Paper sx={{ p: 3, mb: 4, textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                <Typography variant="body1" color="text.secondary">No audit data available.</Typography>
              </Paper>
            )}

            {toast && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {toast}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

export default AuditFindingBlade;
